import React from 'react';
import { View, Pressable, ActivityIndicator, Platform } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { XStack, YStack, SizableText } from 'tamagui';
import Checkbox from 'expo-checkbox';
import { useTranslation } from 'react-i18next';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { getModernCardStyle, getModernTextColor } from '@stylesheet';
import { responsiveFontSize } from '@utils/responsiveFontSize';

export type AreaAccessState = {
  read: boolean;
  write: boolean;
  delete: boolean;
  data: boolean;
  projects: boolean;
  documents: boolean;
  sales: boolean;
};

export const defaultAreaAccess = (): AreaAccessState => ({
  read: true,
  write: false,
  delete: false,
  data: true,
  projects: true,
  documents: true,
  sales: true,
});

interface PermissionsComponentProps {
  areaAccess: AreaAccessState;
  onToggleArea: (key: keyof AreaAccessState) => void;
  loading?: boolean;
  isOwner?: boolean;
}

const PermissionsComponent: React.FC<PermissionsComponentProps> = ({
  areaAccess,
  onToggleArea,
  loading = false,
  isOwner = false,
}) => {
  const { t } = useTranslation();
  const theme = themeSelector();

  const renderAreaRow = (key: keyof AreaAccessState, label: string, description?: string) => (
    <Pressable
      key={key}
      onPress={() => !isOwner && !loading && onToggleArea(key)}
      disabled={isOwner || loading}
      style={[
        getModernCardStyle(theme, 'medium'),
        {
          flexDirection: 'row',
          alignItems: 'center',
          padding: 12,
          marginBottom: 8,
          opacity: isOwner || loading ? 0.55 : 1,
          ...(Platform.OS === 'web' ? { cursor: isOwner || loading ? 'not-allowed' : 'pointer' } : {}),
        },
      ]}
    >
      <Checkbox
        value={areaAccess[key]}
        onValueChange={() => !isOwner && !loading && onToggleArea(key)}
        disabled={isOwner || loading}
        color={theme ? '#2563eb' : '#60a5fa'}
        style={{ marginRight: 12 }}
      />
      <View style={{ flex: 1 }}>
        <SizableText size="$3" fontWeight="600" color={getModernTextColor(theme, 'primary')}>
          {label}
        </SizableText>
        {description ? (
          <SizableText size="$1" color={getModernTextColor(theme, 'muted')}>
            {description}
          </SizableText>
        ) : null}
      </View>
    </Pressable>
  );

  return (
    <YStack gap="$4">
      <XStack alignItems="center" gap={8} marginBottom="$2">
        <MaterialIcons name="admin-panel-settings" size={20} color={theme ? '#0d9488' : '#2dd4bf'} />
        <SizableText size="$4" fontWeight="700" color={getModernTextColor(theme, 'primary')}>
          {t('userAccessAreaSectionTitle')}
        </SizableText>
      </XStack>
      <SizableText size="$2" color={getModernTextColor(theme, 'muted')} marginBottom="$2">
        {t('userAccessAreaSectionHint')}
      </SizableText>

      {loading ? (
        <View style={{ paddingVertical: 20, alignItems: 'center' }}>
          <ActivityIndicator size="large" color={theme ? '#2563eb' : '#60a5fa'} />
        </View>
      ) : (
        <>
          {isOwner && (
            <View
              style={{
                backgroundColor: theme ? '#e0f2fe' : '#1e3a5f',
                padding: 12,
                borderRadius: 10,
                marginBottom: 16,
              }}
            >
              <SizableText size="$3" color={getModernTextColor(theme, 'primary')}>
                {t('userAccessOwnerReadOnly')}
              </SizableText>
            </View>
          )}

          {renderAreaRow('read', t('userAccessPermRead'), t('userAccessPermReadHint'))}
          {renderAreaRow('write', t('userAccessPermWrite'), t('userAccessPermWriteHint'))}
          {renderAreaRow('delete', t('userAccessPermDelete'), t('userAccessPermDeleteHint'))}
          {renderAreaRow('data', t('userAccessModuleData'), t('userAccessModuleDataHint'))}
          {renderAreaRow('projects', t('userAccessModuleProjects'), t('userAccessModuleProjectsHint'))}
          {renderAreaRow('documents', t('userAccessModuleDocuments'), t('userAccessModuleDocumentsHint'))}
          {renderAreaRow('sales', t('userAccessModuleSales'), t('userAccessModuleSalesHint'))}
        </>
      )}
    </YStack>
  );
};

export default PermissionsComponent;
